export type Egm96GridData = {
  south: number;
  west: number;
  spacing: number;
  rows: number;
  columns: number;
  undulations: number[];
};

export class Egm96Grid {
  constructor(private readonly _data: Egm96GridData) {}

  /**
   * The height of the EGM96 geoid above the WGS84 ellipsoid in metres, or 0
   * outside the grid.
   */
  undulationAt(longitudeDegrees: number, latitudeDegrees: number): number {
    const { south, west, spacing, rows, columns } = this._data;
    const columnPosition = (longitudeDegrees - west) / spacing;
    const rowPosition = (latitudeDegrees - south) / spacing;

    const isInsideGrid =
      columnPosition >= 0 &&
      columnPosition <= columns - 1 &&
      rowPosition >= 0 &&
      rowPosition <= rows - 1;
    if (!isInsideGrid) return 0;

    // Clamping keeps a point on the north or east edge inside the last cell.
    const column = Math.min(Math.floor(columnPosition), columns - 2);
    const row = Math.min(Math.floor(rowPosition), rows - 2);
    const columnFraction = columnPosition - column;
    const rowFraction = rowPosition - row;

    const southWest = this.sampleAt(row, column);
    const southEast = this.sampleAt(row, column + 1);
    const northWest = this.sampleAt(row + 1, column);
    const northEast = this.sampleAt(row + 1, column + 1);

    const alongSouthEdge = southWest + (southEast - southWest) * columnFraction;
    const alongNorthEdge = northWest + (northEast - northWest) * columnFraction;
    return alongSouthEdge + (alongNorthEdge - alongSouthEdge) * rowFraction;
  }

  private sampleAt(row: number, column: number) {
    return this._data.undulations[row * this._data.columns + column];
  }
}

let _gridLoad: Promise<Egm96Grid> = null;

export function loadEgm96Grid(): Promise<Egm96Grid> {
  _gridLoad ??= fetchEgm96Grid().catch((error) => {
    _gridLoad = null;
    throw error;
  });
  return _gridLoad;
}

async function fetchEgm96Grid() {
  const response = await fetch("/egm96-australia.json");
  if (!response.ok) {
    throw new Error(`Failed to load the geoid grid (${response.status})`);
  }
  return new Egm96Grid(await response.json());
}
